import * as React from 'react'
import { CaretDown, Check, Plus, X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { badgeVariants } from './badge'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverScroll,
  PopoverTrigger,
} from './popover'

/**
 * MultiCombobox — a searchable multi-select: type to filter, pick several; the
 * chosen options show as removable chips inside the input, and the menu stays
 * open so you can keep adding. react-aria has no multi-combobox (its ComboBox is
 * single-select), so this is a custom build on our Radix `Popover` + a filter
 * input + a virtually-navigated option list (the same engine as the old
 * searchable Select). For single-select search use `Combobox`.
 *
 * The box stays a single row: chips that don't fit collapse into a trailing
 * `+N` badge (same treatment as `OverflowList`/popovers) that opens a scrolling
 * Popover of the hidden chips, rather than the box growing taller. It fills its
 * container width (`w-full`, like `Input`) — size it via the parent or a
 * `className` width.
 *
 *   <MultiCombobox options={[{ value:'a', label:'Apple' }]} value={vals} onChange={setVals} />
 *
 * Set `allowsCustomValue` to let the user add entries that aren't in `options`:
 * when the query matches nothing, an "Add …" row commits the raw text as a value
 * (stored as itself, rendered as its own chip). Good for people/tags not yet in
 * the system.
 */

type ComboSize = 'sm' | 'md' | 'lg'
type ComboStatus = 'default' | 'error' | 'success'

export interface MultiComboboxOption {
  value: string
  label: string
  icon?: React.ReactNode
  disabled?: boolean
}

export interface MultiComboboxProps {
  options: MultiComboboxOption[]
  value?: string[]
  defaultValue?: string[]
  onChange?: (value: string[]) => void
  placeholder?: string
  /**
   * Allow adding values that aren't in `options`: an "Add …" row appears when
   * the query matches no option, committing the raw text as a value.
   */
  allowsCustomValue?: boolean
  /** Shown when the filter matches nothing (and no custom value can be added). */
  emptyMessage?: React.ReactNode
  size?: ComboSize
  status?: ComboStatus
  disabled?: boolean
  className?: string
  'aria-label'?: string
}

const boxSize: Record<ComboSize, string> = {
  sm: 'min-h-8 gap-1 px-1.5 py-1 text-xs',
  md: 'min-h-9 gap-1.5 px-2 py-1 text-sm',
  lg: 'min-h-10 gap-1.5 px-2.5 py-1.5 text-base',
}

const boxStatus: Record<ComboStatus, string> = {
  default:
    'border-fbui-gray-300 focus-within:border-fbui-basil-700 focus-within:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus-within:border-fbui-red-600 focus-within:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus-within:border-fbui-green-600 focus-within:ring-fbui-ring-success',
}

const rowSize: Record<ComboSize, string> = {
  sm: 'gap-2 px-2.5 py-1.5 text-xs',
  md: 'gap-2.5 px-3 py-2 text-sm',
  lg: 'gap-2.5 px-3.5 py-2.5 text-base',
}

const glyph: Record<ComboSize, string> = {
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-4',
}

// Gap between chips (class + matching px for the fit calculation).
const chipGapClass: Record<ComboSize, string> = {
  sm: 'gap-1',
  md: 'gap-1.5',
  lg: 'gap-1.5',
}
const chipGapPx: Record<ComboSize, number> = { sm: 4, md: 6, lg: 6 }

// Reserved widths (px) the fit calc keeps clear: a usable filter input, the
// clear-all button and the caret, so chips never crowd them out before
// collapsing into the `+N` badge.
const INPUT_MIN = 48
const CARET_W = 16
const CLEAR_W = 24

export const MultiCombobox = React.forwardRef<
  HTMLInputElement,
  MultiComboboxProps
>(
  (
    {
      options,
      value,
      defaultValue,
      onChange,
      placeholder = 'Select…',
      allowsCustomValue = false,
      emptyMessage = 'No results',
      size = 'md',
      status = 'default',
      disabled = false,
      className,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const isControlled = value !== undefined
    const [internal, setInternal] = React.useState<string[]>(defaultValue ?? [])
    const selected = isControlled ? value : internal
    const [open, setOpen] = React.useState(false)
    const [query, setQuery] = React.useState('')
    const [activeIndex, setActiveIndex] = React.useState(0)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const boxRef = React.useRef<HTMLDivElement>(null)
    const contentRef = React.useRef<HTMLDivElement>(null)
    const mirrorRef = React.useRef<HTMLDivElement>(null)
    const rowRefs = React.useRef<(HTMLDivElement | null)[]>([])
    const [visibleCount, setVisibleCount] = React.useState(selected.length)
    const listboxId = React.useId()
    // Key by the stable option value (not array index) so aria-activedescendant
    // can't point at a stale/wrong row as the filtered list changes.
    const optionId = (value: string) => `${listboxId}-opt-${value}`

    // Resolve each selected value to its option; a value with no matching option
    // is a custom (creatable) entry, rendered with the value as its own label.
    const selectedOptions: MultiComboboxOption[] = selected.map(
      (v) => options.find((o) => o.value === v) ?? { value: v, label: v }
    )

    // Measure how many chips fit on one row (reserving room for the input,
    // caret and the `+N` badge) against a hidden full-width mirror; recompute on
    // selection change and container resize. Hiding chips never widens the
    // constrained row, so there's no measure loop. (No CLAUDE.md no-useEffect
    // rule applies in fb-ui; this mirrors OverflowList.)
    React.useLayoutEffect(() => {
      const measure = () => {
        const content = contentRef.current
        const mirror = mirrorRef.current
        if (!content || !mirror) return
        const gap = chipGapPx[size]
        const available =
          content.clientWidth - INPUT_MIN - CARET_W - CLEAR_W - gap * 3
        const nodes = Array.from(mirror.children) as HTMLElement[]
        const badgeWidth = nodes[nodes.length - 1]?.offsetWidth ?? 0
        const widths = nodes
          .slice(0, selectedOptions.length)
          .map((n) => n.offsetWidth)
        let used = 0
        let count = 0
        for (let i = 0; i < selectedOptions.length; i++) {
          const w = widths[i] + (i > 0 ? gap : 0)
          const remainingAfter = selectedOptions.length - (i + 1)
          const reserve = remainingAfter > 0 ? gap + badgeWidth : 0
          if (used + w + reserve <= available) {
            used += w
            count++
          } else break
        }
        setVisibleCount((prev) => (prev === count ? prev : count))
      }
      measure()
      const content = contentRef.current
      if (!content || typeof ResizeObserver === 'undefined') return
      const observer = new ResizeObserver(measure)
      observer.observe(content)
      return () => observer.disconnect()
    }, [selected, options, size])

    const filtered = query
      ? options.filter((o) =>
          o.label.toLowerCase().includes(query.toLowerCase())
        )
      : options

    // Offer an "Add …" row when custom values are allowed and the (trimmed)
    // query is new — not already selected and not an existing option label.
    const trimmedQuery = query.trim()
    const canCreate =
      allowsCustomValue &&
      trimmedQuery.length > 0 &&
      !selected.includes(trimmedQuery) &&
      !options.some((o) => o.label.toLowerCase() === trimmedQuery.toLowerCase())
    const createOption: MultiComboboxOption = {
      value: trimmedQuery,
      label: `Add “${trimmedQuery}”`,
      icon: <Plus weight="bold" />,
    }
    // The navigable + rendered list: filtered options, plus the create row.
    const items = canCreate ? [...filtered, createOption] : filtered

    const commit = (next: string[]) => {
      if (!isControlled) setInternal(next)
      onChange?.(next)
    }

    const toggle = (opt: MultiComboboxOption) => {
      if (opt.disabled) return
      commit(
        selected.includes(opt.value)
          ? selected.filter((v) => v !== opt.value)
          : [...selected, opt.value]
      )
      setQuery('')
      // The query reset restores the full list, so the old filtered index is
      // meaningless — point the highlight back at the top.
      setActiveIndex(0)
      inputRef.current?.focus()
    }

    const removeAt = (val: string) => commit(selected.filter((v) => v !== val))

    const moveActive = (dir: 1 | -1) => {
      const n = items.length
      if (!n) return
      let i = activeIndex
      for (let step = 0; step < n; step++) {
        i = (i + dir + n) % n
        if (!items[i].disabled) break
      }
      setActiveIndex(i)
      rowRefs.current[i]?.scrollIntoView({ block: 'nearest' })
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (!open) setOpen(true)
        else moveActive(1)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (!open) setOpen(true)
        else moveActive(-1)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const opt = items[activeIndex]
        if (opt) toggle(opt)
      } else if (e.key === 'Backspace' && !query && selected.length) {
        // Don't let Backspace remove a disabled selection (its chip × is
        // disabled too).
        const last = selected[selected.length - 1]
        if (options.find((o) => o.value === last)?.disabled) return
        commit(selected.slice(0, -1))
      } else if (e.key === 'Escape') {
        setOpen(false)
      }
    }

    const renderChip = (opt: MultiComboboxOption) => (
      <span
        key={opt.value}
        className="inline-flex shrink-0 items-center gap-1 rounded-md bg-fbui-gray-100 py-0.5 pl-2 pr-1 text-xs text-fbui-gray-700"
      >
        {opt.label}
        <button
          type="button"
          disabled={disabled || opt.disabled}
          aria-label={`Remove ${opt.label}`}
          onClick={() => removeAt(opt.value)}
          className="inline-flex size-4 items-center justify-center rounded text-fbui-gray-500 outline-none transition-colors hover:bg-fbui-gray-200 hover:text-fbui-gray-600 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
        >
          <X weight="bold" className="size-3" aria-hidden="true" />
        </button>
      </span>
    )

    const visible = selectedOptions.slice(0, visibleCount)
    const hidden = selectedOptions.slice(visibleCount)

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div
            ref={boxRef}
            onMouseDown={(e) => {
              // Clicking blank box area focuses the input — but never steal a
              // click meant for a chip's remove button or the `+N` badge.
              const t = e.target as HTMLElement
              if (t === inputRef.current || t.closest('button')) return
              e.preventDefault()
              inputRef.current?.focus()
              setOpen(true)
            }}
            className={cn(
              'relative flex w-full items-center overflow-hidden rounded-lg border bg-fbui-base-white text-fbui-gray-800 transition-colors focus-within:ring-4',
              boxSize[size],
              boxStatus[status],
              disabled && 'cursor-not-allowed bg-fbui-gray-50',
              className
            )}
          >
            <div
              ref={contentRef}
              className={cn(
                'flex w-full min-w-0 items-center',
                chipGapClass[size]
              )}
            >
              {selectedOptions.length > 0 && (
                <div
                  className={cn(
                    'flex min-w-0 items-center overflow-hidden',
                    chipGapClass[size]
                  )}
                >
                  {visible.map(renderChip)}
                  {hidden.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          disabled={disabled}
                          aria-label={`Show ${hidden.length} more selected`}
                          onClick={() => setOpen(false)}
                          className={cn(
                            badgeVariants({ color: 'gray', size: 'sm' }),
                            'shrink-0 cursor-pointer outline-none transition-colors hover:bg-fbui-gray-200 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand data-[state=open]:bg-fbui-gray-200'
                          )}
                        >
                          +{hidden.length}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        size="sm"
                        align="start"
                        onCloseAutoFocus={(e) => e.preventDefault()}
                        className="w-max max-w-xs"
                      >
                        <PopoverScroll
                          maxHeight="15rem"
                          className={cn(
                            'flex flex-col items-start',
                            chipGapClass[size]
                          )}
                        >
                          {hidden.map(renderChip)}
                        </PopoverScroll>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}
              <input
                ref={(node) => {
                  inputRef.current = node
                  if (typeof ref === 'function') ref(node)
                  else if (ref) ref.current = node
                }}
                type="text"
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listboxId : undefined}
                aria-activedescendant={
                  open && items[activeIndex]
                    ? optionId(items[activeIndex].value)
                    : undefined
                }
                aria-label={ariaLabel}
                disabled={disabled}
                value={query}
                placeholder={selected.length ? '' : placeholder}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActiveIndex(0)
                  setOpen(true)
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                className="min-w-12 flex-1 bg-transparent px-1 py-0.5 text-fbui-gray-800 outline-none placeholder:text-fbui-gray-400 disabled:cursor-not-allowed"
              />
              {!disabled && (selected.length > 0 || query) && (
                <button
                  type="button"
                  aria-label="Clear all"
                  onClick={() => {
                    // Keep locked (disabled) selections — the user can't re-add
                    // them, so Clear All mustn't silently drop them either.
                    commit(
                      selected.filter(
                        (v) => options.find((o) => o.value === v)?.disabled
                      )
                    )
                    setQuery('')
                    inputRef.current?.focus()
                  }}
                  className="inline-flex size-6 shrink-0 items-center justify-center self-center rounded text-fbui-gray-500 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-600 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
                >
                  <X weight="bold" className="size-3.5" aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                // Not a tab stop — the input is the control; this is a
                // pointer affordance that toggles the menu (a bare click on
                // the box only opens).
                tabIndex={-1}
                disabled={disabled}
                aria-label={open ? 'Hide suggestions' : 'Show suggestions'}
                aria-expanded={open}
                // Keep focus on the input so toggling closed doesn't blur it
                // (a blur→refocus would let the input's onFocus reopen the menu).
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setOpen((o) => !o)}
                className="inline-flex size-6 shrink-0 items-center justify-center self-center rounded text-fbui-gray-500 outline-none transition-colors hover:text-fbui-gray-600 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand disabled:cursor-not-allowed"
              >
                <CaretDown
                  weight="bold"
                  aria-hidden="true"
                  className="size-3.5"
                />
              </button>
            </div>
            {/* Hidden full-width mirror — measures natural chip widths + a
                sample `+N` badge for the fit calculation. */}
            <div
              ref={mirrorRef}
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute left-0 top-0 flex w-max items-center opacity-0',
                chipGapClass[size]
              )}
            >
              {selectedOptions.map(renderChip)}
              <span
                className={cn(
                  badgeVariants({ color: 'gray', size: 'sm' }),
                  'shrink-0'
                )}
              >
                +{selectedOptions.length}
              </span>
            </div>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // Pointer/focus on our own box (the input, a chip's remove button,
          // the caret) must not dismiss the menu — that's what keeps it open
          // through repeated picks and lets a plain click open it.
          onInteractOutside={(e) => {
            if (boxRef.current?.contains(e.target as Node)) e.preventDefault()
          }}
          onFocusOutside={(e) => {
            if (boxRef.current?.contains(e.target as Node)) e.preventDefault()
          }}
          className="w-[var(--radix-popover-trigger-width)] p-1.5"
        >
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-fbui-gray-500">
              {emptyMessage}
            </div>
          ) : (
            <PopoverScroll
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel ?? 'Options'}
              aria-multiselectable
              className="max-h-72"
            >
              {items.map((opt, i) => {
                const isSelected = selected.includes(opt.value)
                return (
                  <div
                    key={opt.value}
                    id={optionId(opt.value)}
                    ref={(el) => {
                      rowRefs.current[i] = el
                    }}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled || undefined}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => toggle(opt)}
                    className={cn(
                      'flex cursor-pointer items-center rounded-lg text-fbui-gray-700 outline-none select-none',
                      rowSize[size],
                      i === activeIndex &&
                        'bg-fbui-gray-100 text-fbui-gray-900',
                      isSelected && 'font-medium text-fbui-basil-700',
                      opt.disabled && 'pointer-events-none text-fbui-gray-300'
                    )}
                  >
                    {opt.icon && (
                      <span
                        className={cn(
                          'flex shrink-0 items-center justify-center text-fbui-gray-500',
                          glyph[size]
                        )}
                      >
                        {opt.icon}
                      </span>
                    )}
                    <span className="flex-1 truncate">{opt.label}</span>
                    {isSelected && (
                      <Check
                        weight="bold"
                        aria-hidden="true"
                        className={cn(
                          'shrink-0 text-fbui-basil-700',
                          glyph[size]
                        )}
                      />
                    )}
                  </div>
                )
              })}
            </PopoverScroll>
          )}
        </PopoverContent>
      </Popover>
    )
  }
)
MultiCombobox.displayName = 'MultiCombobox'
